import { useRef, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageHeroDecor from '@/components/feature/PageHeroDecor';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';
import { useSEO } from '@/hooks/useSEO';

const philosophies = [
  {
    num: '01',
    en: 'Meaning First',
    title: '意味が先、手段は後。',
    body: '多くの組織は、「何を発信するか」より先に「どう発信するか」を考えます。SNS、動画、パンフレット——手段の選択が先行し、肝心の「なぜ、この組織が存在するのか」が曖昧なまま発信が続きます。私たちはその順序を逆にします。まず意味を掘り起こし、言語化し、構造化する。手段はその後です。',
  },
  {
    num: '02',
    en: 'Translation',
    title: '専門性は、翻訳されて初めて届く。',
    body: '医療・福祉・教育・地域・文化の現場には、深い専門性と誠実な実践があります。しかしそれは、そのままでは社会に届きません。専門用語は壁になり、謙虚さは沈黙になり、誠実さは見えなくなる。私たちの仕事は、その専門性を「社会の言葉」に翻訳することです。',
  },
  {
    num: '03',
    en: 'Circulation',
    title: '発信ではなく、循環を設計する。',
    body: '一方向の発信は、受け手を「消費者」にとどめます。私たちが目指すのは、関わる人が自ら語り、広め、参加したくなる構造です。患者が語り手になる。卒業生が語り手になる。地域住民が語り手になる。そのような循環が生まれたとき、組織の意味は社会に根を張ります。',
  },
  {
    num: '04',
    en: 'Legacy',
    title: '今の仕事を、未来の資産にする。',
    body: 'プロジェクトが終わっても、組織に残る力を育てることを意識します。私たちは「納品」で終わる仕事をしません。言葉が組織の文化になり、スタッフが自ら語れるようになり、3年後も誇れる資産として蓄積される——そのような設計を、常に意識しています。',
  },
];

const differences = [
  { label: '従来のアプローチ', items: ['依頼された制作物をつくる', '手段（媒体・デザイン）から考える', 'プロジェクト完了で関係終了', '成果物の品質を重視'] },
  { label: '私たちのアプローチ', items: ['意味の言語化から始める', '目的（意味・価値）から考える', '循環する構造を育てる', '組織に残る力を重視'], highlight: true },
];

function FadeIn({ children, delay = 0, className = '', from = 'bottom' }: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  from?: 'bottom' | 'left' | 'right';
}) {
  const { ref, isVisible } = useScrollAnimation();
  const translateMap = { bottom: 'translateY(28px)', left: 'translateX(-28px)', right: 'translateX(28px)' };
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate(0)' : translateMap[from],
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

function PhilosophyList() {
  const { ref, visibleCount } = useStaggerAnimation(philosophies.length);
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="space-y-0 border-t border-divider">
      {philosophies.map((p, idx) => (
        <div
          key={p.num}
          className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 py-16 md:py-20 border-b border-divider"
          style={{
            opacity: visibleCount > idx ? 1 : 0,
            transform: visibleCount > idx ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <div className="md:col-span-1">
            <span className="font-garamond text-4xl font-light text-divider leading-none">{p.num}</span>
          </div>
          <div className="md:col-span-4">
            <span className="font-garamond text-xs italic text-muted-light tracking-widest-jp block mb-4">{p.en}</span>
            <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-snug text-2xl md:text-3xl">
              {p.title}
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{p.body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function KangaekataPage() {
  useSEO({
    title: '考え方・フィロソフィー | メセナインターコム株式会社',
    description: 'メセナインターコム株式会社が大切にしている思想と仕事の根底にある考え方。「意味が先、手段は後」「専門性は翻訳されて初めて届く」「発信ではなく循環を設計する」——医療・福祉・教育・地域・文化の価値創造への哲学。',
    keywords: '考え方,フィロソフィー,意味設計,価値創造,翻訳,循環,医療,福祉,教育,コンサルティング',
    ogTitle: '考え方・フィロソフィー | メセナインターコム株式会社',
    ogDescription: '「意味が先、手段は後」——メセナインターコム株式会社が大切にしている思想と、仕事の根底にある考え方をお伝えします。',
    ogUrl: 'https://mecena-intercom.co.jp/kangaekata',
    canonical: 'https://mecena-intercom.co.jp/kangaekata',
  });

  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }, []);

  return (
    <div className="bg-warm-white min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-40 pb-28 md:pt-52 md:pb-40 bg-warm-white relative overflow-hidden">
          <PageHeroDecor variant="amber" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div
                ref={heroRef}
                className="md:col-span-8 md:col-start-3"
                style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.9s ease 0.1s, transform 0.9s ease 0.1s' }}
              >
                <div className="flex items-center gap-4 mb-10">
                  <span className="block w-6 h-px bg-accent-teal" />
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Philosophy</span>
                </div>
                <h1 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-4xl md:text-5xl mb-12">
                  考え方
                </h1>
                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp max-w-xl">
                  広告制作会社として、私たちが大切にしている思想と仕事の根底にある考え方をお伝えします。<br />
                  「なぜ、この広告をつくるのか」——その問いへの、私たちなりの答えです。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy items */}
        <section className="texture-philosophy py-20 md:py-32 bg-warm-light relative overflow-hidden">
          {/* 「意味の発掘・翻訳」テーマ装飾 */}
          {/* 左上の「言語の断片」 */}
          <div className="absolute top-0 left-0 w-32 h-32 lg:w-56 lg:h-56 pointer-events-none overflow-hidden">
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(212,122,46,0.18) 1.5px, transparent 1.5px)', backgroundSize: '18px 18px' }} />
          </div>
          {/* 右下の同心円（意味が広がる） */}
          <div className="absolute -bottom-16 -right-16 w-[200px] h-[200px] lg:w-[400px] lg:h-[400px] rounded-full border border-accent-amber/14 pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 w-[320px] h-[320px] lg:w-[600px] lg:h-[600px] rounded-full border border-accent-gold/9 pointer-events-none hidden sm:block" />
          {/* 各哲学に対応した菱形（4つ） */}
          <div className="absolute top-[15%] right-8 lg:right-16 w-5 h-5 lg:w-10 lg:h-10 border border-accent-amber/30 rotate-45 pointer-events-none" />
          <div className="absolute top-[35%] right-12 lg:right-24 w-3 h-3 lg:w-6 lg:h-6 border border-accent-gold/35 rotate-45 pointer-events-none" />
          <div className="absolute top-[55%] right-8 lg:right-16 w-4 h-4 lg:w-8 lg:h-8 border border-accent-teal/28 rotate-45 pointer-events-none" />
          <div className="absolute top-[75%] right-12 lg:right-24 w-3 h-3 lg:w-6 lg:h-6 border border-accent-amber/32 rotate-45 pointer-events-none" />
          {/* 縦の「翻訳の流れ」を示す線 */}
          <div className="absolute right-6 top-[10%] pointer-events-none hidden lg:block" style={{ width: 1, height: '80%', background: 'linear-gradient(to bottom, transparent, rgba(212,122,46,0.12), rgba(74,158,142,0.12), transparent)' }} />
          <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
            <PhilosophyList />
          </div>
        </section>

        {/* Difference */}
        <section className="py-20 md:py-32 bg-warm-white relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <FadeIn delay={0}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
                <div className="md:col-span-6">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="block w-6 h-px bg-accent-teal" />
                    <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Difference</span>
                  </div>
                  <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl md:text-4xl">
                    私たちが大切にしていること
                  </h2>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-divider border border-divider">
                {differences.map((d) => (
                  <div key={d.label} className={`p-10 md:p-14 flex flex-col gap-8 ${d.highlight ? 'bg-charcoal' : 'bg-warm-white'}`}>
                    <p className={`font-sans-jp text-xs tracking-widest-jp uppercase ${d.highlight ? 'text-accent-teal' : 'text-muted-light'}`}>{d.label}</p>
                    <div className="space-y-5">
                      {d.items.map((item) => (
                        <div key={item} className="flex items-start gap-4">
                          <span className={`block w-1 h-1 rounded-full flex-shrink-0 mt-2 ${d.highlight ? 'bg-accent-teal' : 'bg-divider'}`} />
                          <span className={`font-serif-jp text-base font-light tracking-normal-jp leading-snug ${d.highlight ? 'text-warm-white' : 'text-muted-brown'}`}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>

        {/* Quote */}
        <section className="texture-message py-20 md:py-32 bg-warm-light relative overflow-hidden">
          {/* 「意味はすでにそこにある」テーマ装飾 */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] lg:w-[500px] lg:h-[500px] rounded-full border border-accent-gold/10 pointer-events-none" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] lg:w-[340px] lg:h-[340px] rounded-full border border-accent-amber/12 pointer-events-none" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100px] h-[100px] lg:w-[180px] lg:h-[180px] rounded-full border border-accent-gold/16 pointer-events-none" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
            <FadeIn delay={0}>
              <div className="grid grid-cols-1 md:grid-cols-12">
                <div className="md:col-span-8 md:col-start-3 text-center">
                  <blockquote className="font-garamond text-2xl md:text-3xl italic text-charcoal leading-relaxed mb-10">
                    &ldquo;The meaning is already there.<br />We just help you see it.&rdquo;
                  </blockquote>
                  <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
                    意味は、すでにそこにある。<br />
                    私たちは、それを見えるようにするお手伝いをするだけです。
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28 bg-warm-white border-t border-divider">
          <FadeIn delay={0}>
            <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div>
                <h3 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-2xl mb-3">
                  私たちの広告・制作に興味を持っていただけましたか？
                </h3>
                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
                  まずは30分、課題とつくるべきものを一緒に整理しましょう。
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
                <Link to="/dekirukoto" className="inline-flex items-center gap-2 px-7 py-3.5 border border-charcoal text-sm font-sans-jp text-charcoal tracking-wide-jp hover:bg-charcoal hover:text-warm-white transition-all duration-300 whitespace-nowrap">
                  できることを見る →
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 bg-charcoal text-warm-white text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal-soft transition-colors duration-300 whitespace-nowrap">
                  相談してみる
                </Link>
              </div>
            </div>
          </FadeIn>
        </section>
      </main>
      <Footer />
    </div>
  );
}
